import { Form, Group, Text, useMelioForm } from '@melio/penny';
import { AutoPaymentAmountTypeEnum, AutoPaymentDeliveryDateTypeEnum } from '@melio/platform-api';
import { useMelioIntl } from '@melio/platform-i18n';
import { useEffect } from 'react';

import { useAutoPaymentActivationFormColumnSpan } from './hooks/useAutoPaymentActivationFormColumnSpan';
import { useAutoPaymentActivationFormSchema } from './hooks/useAutoPaymentActivationFormSchema';
import { useAutoPaymentOptionsFormValues } from './hooks/useAutoPaymentOptionsFormValues';
import { AutoPaymentActivationFormFields, AutoPaymentActivationFormProps } from './types';

export const AutoPaymentActivationForm = ({
  vendor,
  autoPaymentOptions,
  fundingSources,
  account,
  isSaving,
  onSubmit,
  onSubmissionStateChange,
  autoPayment,
}: AutoPaymentActivationFormProps) => {
  const { formatMessage, formatCurrency } = useMelioIntl();
  const { fullColumnSpan, mainColumnSpan, secondaryColumnSpan } = useAutoPaymentActivationFormColumnSpan();
  const {
    defaultValues,
    autoPaymentFundingSourceOptions,
    deliveryDateTypeOptions,
    scheduleDaysBeforeOptions,
    paymentAmountTypeOptions,
  } = useAutoPaymentOptionsFormValues({ autoPaymentOptions, vendor, fundingSources, autoPayment });

  const { formProps, registerField, watch, setValue, resetField } = useMelioForm<AutoPaymentActivationFormFields>({
    defaultValues,
    isSaving,
    onSubmit,
    onSubmissionStateChange,
    schema: useAutoPaymentActivationFormSchema(autoPaymentOptions),
  });

  const fundingSourceId = watch('fundingSourceId');
  const deliveryDateType = watch('deliveryDateType');
  const paymentAmountType = watch('paymentAmountType');

  const selectedAutoPaymentFundingSource = autoPaymentOptions.autoPaymentFundingSources.find(
    (autoPaymentFundingSource) => autoPaymentFundingSource.fundingSourceId === fundingSourceId
  );

  useEffect(() => {
    if (deliveryDateType !== AutoPaymentDeliveryDateTypeEnum.DaysBeforeDueDate) {
      setValue('scheduleDaysBefore', null);
    } else {
      resetField('scheduleDaysBefore');
    }
    if (paymentAmountType !== AutoPaymentAmountTypeEnum.FixedAmount) {
      setValue('fixedAmount', null);
    } else {
      setValue('maxAmount', null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryDateType, paymentAmountType]);

  const isDaysBeforeDueDate = deliveryDateType === AutoPaymentDeliveryDateTypeEnum.DaysBeforeDueDate;
  const isFixedAmount = paymentAmountType === AutoPaymentAmountTypeEnum.FixedAmount;

  return (
    <Form columns={fullColumnSpan} {...formProps} data-testid="auto-payment-activation-form">
      <Form.TextField
        {...registerField('accountNumber')}
        labelProps={{
          label: formatMessage(
            'activities.vendor.autoPaymentActivationForm.screens.activation.form.accountNumber.label'
          ),
        }}
        colSpan={fullColumnSpan}
        isReadOnly
      />
      <Form.Select
        {...registerField('fundingSourceId')}
        labelProps={{
          label: formatMessage(
            'activities.vendor.autoPaymentActivationForm.screens.activation.form.fundingSourceId.label'
          ),
        }}
        options={autoPaymentFundingSourceOptions}
        emptyState={undefined}
        colSpan={fullColumnSpan}
      />
      <Form.Select
        {...registerField('deliveryDateType')}
        labelProps={{
          label: formatMessage(
            'activities.vendor.autoPaymentActivationForm.screens.activation.form.deliveryDateType.label'
          ),
        }}
        options={deliveryDateTypeOptions}
        emptyState={undefined}
        colSpan={isDaysBeforeDueDate ? mainColumnSpan : fullColumnSpan}
      />
      <Form.Select
        {...registerField('scheduleDaysBefore')}
        labelProps={{
          label: formatMessage(
            'activities.vendor.autoPaymentActivationForm.screens.activation.form.scheduleDaysBefore.label'
          ),
        }}
        options={scheduleDaysBeforeOptions}
        emptyState={undefined}
        colSpan={secondaryColumnSpan}
        isHidden={!isDaysBeforeDueDate}
      />

      <Form.Select
        {...registerField('paymentAmountType')}
        labelProps={{
          label: formatMessage(
            'activities.vendor.autoPaymentActivationForm.screens.activation.form.paymentAmountType.label'
          ),
        }}
        options={paymentAmountTypeOptions}
        emptyState={undefined}
        colSpan={mainColumnSpan}
      />
      <Form.AmountField
        {...registerField('fixedAmount')}
        labelProps={{
          label: formatMessage('activities.vendor.autoPaymentActivationForm.screens.activation.form.fixedAmount.label'),
        }}
        helperTextProps={
          selectedAutoPaymentFundingSource
            ? {
                label: formatMessage(
                  'activities.vendor.autoPaymentActivationForm.screens.activation.form.fixedAmount.helperText',
                  {
                    maxFixedAmount: formatCurrency(selectedAutoPaymentFundingSource.maxFixedAmount),
                    minFixedAmount: formatCurrency(selectedAutoPaymentFundingSource.minFixedAmount),
                  }
                ),
              }
            : undefined
        }
        colSpan={secondaryColumnSpan}
        isHidden={!isFixedAmount}
      />
      <Form.AmountField
        {...registerField('maxAmount')}
        labelProps={{
          label: formatMessage('activities.vendor.autoPaymentActivationForm.screens.activation.form.maxAmount.label'),
        }}
        helperTextProps={{
          label: formatMessage(
            'activities.vendor.autoPaymentActivationForm.screens.activation.form.maxAmount.helperText'
          ),
        }}
        colSpan={secondaryColumnSpan}
        isHidden={isFixedAmount}
      />
      <Form.ContentBox colSpan={fullColumnSpan}>
        <Text
          textStyle="body4Semi"
          color="neutral.darker"
          data-testid="auto-payment-activation-form-notification-content"
        >
          {formatMessage(
            'activities.vendor.autoPaymentActivationForm.screens.activation.form.notificationContent.label',
            { email: account.user.email }
          )}
        </Text>
      </Form.ContentBox>
      <Form.ContentBox colSpan={fullColumnSpan}>
        <Group variant="vertical">
          <Group variant="horizontal" justifyContent="space-between" data-testid="should-notify-on-scheduled-container">
            <Text>
              {formatMessage(
                'activities.vendor.autoPaymentActivationForm.screens.activation.form.shouldNotifyOnScheduled.label'
              )}
            </Text>
            <Form.Switch {...registerField('shouldNotifyOnScheduled')} labelProps={{ label: '' }} />
          </Group>
          <Group variant="horizontal" justifyContent="space-between" data-testid="should-notify-on-sent-container">
            <Text>
              {formatMessage(
                'activities.vendor.autoPaymentActivationForm.screens.activation.form.shouldNotifyOnSent.label'
              )}
            </Text>
            <Form.Switch {...registerField('shouldNotifyOnSent')} labelProps={{ label: '' }} />
          </Group>
        </Group>
      </Form.ContentBox>
    </Form>
  );
};
